import React, { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import Head from 'next/head';
import Image from "next/legacy/image";
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { priceToNumber } from 'functions/bagPrice';
import { getAccessorieName, getProductName } from 'functions/getProductName';
import { formatVisiblePrice } from 'utils/common.utils';
import { BackgroundColor, generateAccessoriesImage, generateAccessoriesImageMultiple } from 'utils/image.utils';
import Breadcrumb from 'components/Breadcrumb/Breadcrumb';
import Button from 'components/Button/Button';
import ProdSliderModal from 'components/ProdSlider/ProdSliderModal';
import Spinner from 'components/Spinner/Spinner';

import { getLocalCurrency } from 'functions/currency';
import {
  CREATE_CART,
  UPDATE_QUANTITY_CART
} from 'gqlStrings/mutations/addToCart';
import { addDataLayer } from 'lib/ga';
import { sendAddToCartEvent } from 'lib/tracking';
import {
  setLoading,
  updateShoppingCartWithRequest
} from 'redux/features/bag/bagSlice';
import { RootState } from 'redux/store';
import UITabs, { UITab } from 'components/Others/UITabs';
import UIAccordion from 'components/Others/UIAccordion';
import { ProductDetailType, SeoBlock } from 'constants/dataTypes';
import { slugToString, stringToSlug } from 'functions/slug';
import { addToCart } from 'redux/features/cart/cartSlice';
import LazyHydrate from 'react-lazy-hydration';
import CategorySeoText from 'components/Others/CategorySeoText';
import FAQJson from 'components/Others/FAQJson';
import PriceBadge from 'components/Others/PriceBadge';
import AfterPayInfo from 'components/Others/AfterPayInfo';
import ZipPayInfo from 'components/Others/ZipPayInfo';
import { getSession } from 'functions/session';
import UITooltip from 'components/Others/UITooltip';
import Checkmark from 'components/Icons/Checkmark';
import cx from 'classnames';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Swiper as SwiperInstance, Thumbs } from 'swiper';
import Arrow from 'components/Icons/Arrow';
import 'swiper/css';
import { imageLoader } from 'functions/imageLoader';
import { PrideDiscountBanner } from 'components/ProductDetail/PrideDiscountBanner';

interface CategoryInfo {
  name: string;
  slug: string;
}

interface ProductInfo {
  name: string;
  slug: string;
}

export default function ProductComponent({
  categoryInfo,
  productInfo,
  product,
  productDetailList,
  seoBlock,
  accessories
}: {
  categoryInfo: CategoryInfo;
  productInfo: ProductInfo;
  product: any;
  productDetailList?: ProductDetailType[];
  seoBlock: SeoBlock;
  accessories: any;
}) {

  const productDetail = useMemo(() => {
    if (productDetailList != null) {
      const productDetailInfoFromCategory = productDetailList!.filter((productDetail) => {
        return productDetail.slug === stringToSlug(categoryInfo.name)
      });
      if (productDetailInfoFromCategory.length > 0) {
        return { description: productDetailInfoFromCategory[0].description, title: productDetailInfoFromCategory[0].sectionTitle }
      }
      else {
        const productDetailInfoFromProduct = productDetailList!.filter((productDetail) => {
          return productDetail.slug === stringToSlug(productInfo.name)
        });
        if (productDetailInfoFromProduct.length > 0) {
          return { description: productDetailInfoFromProduct[0].description, title: productDetailInfoFromProduct[0].sectionTitle }
        }
      }
      return null;
    }
    return null;
  }, [categoryInfo.name, productDetailList, productInfo.name]);
  //console.log('productDetail', productDetail);
  const router = useRouter();
  const uuidV4 = uuidv4();
  const dispatch = useDispatch();
  const [modalOpen, setModalOpen] = useState(false);
  const [isImported, setIsImported] = useState(false);
  const isLoad = useSelector((state: RootState) => state.bagEvent.loading);
  const bagItems = useSelector((state: RootState) => state.bagEvent.bag);
  const [swiperInstance, setSwiperInstance] = useState<SwiperInstance | null>(
    null
  );
  const [swiperActiveIndex, setSwiperActiveIndex] = useState(0);

  (swiperInstance && !swiperInstance.destroyed) && swiperInstance.on('activeIndexChange', () => {
    setSwiperActiveIndex(swiperInstance.realIndex)
  })

  const navigateToCarouselSlide = (index: number) => {
    swiperInstance?.slideTo(index + 1);
    swiperInstance?.update();
  };


  const visiblePrice = useMemo(() => {
    return {
      salePrice: product.meta.price,
      regularPrice: product.meta.regular_price
    };
  }, [product]);

  const data = product && [
    {
      id: 1,
      href: '',
      text: 'Dresden Vision'
    },
    {
      id: 2,
      href: 'shop',
      text: 'Shop'
    },
    {
      id: 3,
      href: 'shop/accessories',
      text: 'Accessories'
    },
    {
      id: 4,
      href: `shop/accessories/${categoryInfo.slug}`,
      text: categoryInfo.name
    },
    {
      id: 5,
      href: `/shop/accessories/${productInfo.slug}`,
      text: productInfo.name
    }
  ];

  const [mutateCreateCart] = useMutation(CREATE_CART, {
    context: { clientName: 'laravel' }
  });

  useEffect(() => {
    if (process.browser && !getSession()) {
      mutateCreateCart({
        variables: {
          input: {
            isEmpty: true
          }
        }
      });
    }
  }, [mutateCreateCart]);


  const cdnImage = useMemo(() => product?.name && generateAccessoriesImage(product?.slug), [product]);

  const [mutationUpdateItemQuatity] = useMutation(UPDATE_QUANTITY_CART, {
    context: { locale: router.locale },
    onCompleted: (getCartData) => {
      dispatch(
        updateShoppingCartWithRequest(
          getCartData.updateQuantityCompositeItemInCart
        )
      );
      dispatch(setLoading(false));
    },
    onError: (error) => {
      console.log(error);
    }
  });

  if (router.isFallback) {
    return (
      <div className="h-[70vh] items-center justify-center flex">
        Loading...
      </div>
    );
  }

  const getSeoImage = () => {
    return generateAccessoriesImage(getAccessorieName(product.name));
  }

  const colorHexArray = [
    { name: "black", hex: "#000000" },
    { name: "yellow", hex: "#F2EB2D" },
    { name: "red", hex: "#FF0000" },
    { name: "hot-pink", hex: "#FF69B4" },
    { name: "lime-green", hex: "#32CD32" },
    { name: "moss-green", hex: "#8A9A5B" },
    { name: "orange", hex: "#FFA500" },
    { name: "burgundy", hex: "#800020" },
    { name: "light-grey", hex: "#D3D3D3" },
    { name: "dark-grey", hex: "#A9A9A9" },
    { name: "blue", hex: "#0f59be" }
  ];

  const getColorHex = (color: string) => {
    const colorHex = colorHexArray.filter((e) => e.name === color);
    if (colorHex.length > 0) {
      return colorHex[0].hex;
    }
    return '#000';
  }

  const feltCasesColors = useMemo(() => {
    const products = accessories.products;
    const feltCases = products.filter((product: any) => product.slug.includes('felt-case'));
    const colors = feltCases.map((product: any) => {
      if (product && product.meta && product.meta['attribute_pa_felt-case-color']) {
        const color = product.meta['attribute_pa_felt-case-color'];
        return color;
      }
    });
    return colors;
  }, [accessories]);

  const isFeltCase = useMemo(() => {
    return product.slug.includes('felt-case');
  }, [product]);

  const feltCaseColor = useMemo(() => {
    if (isFeltCase) {
      return product.meta['attribute_pa_felt-case-color'];
    }
  }, [product]);

  const isGlassesStraps = useMemo(() => {
    return product.slug.includes('glasses-straps');
  }, [product]);

  const getStrapColorName = (color: string) => {
    return color.replace('glasses-straps-', '');
  }

  const images = useMemo(() => {
    let images = [cdnImage];
    if (product.meta['image_count'] && product.meta['image_count'] > 1) {
      for (let i = 1; i <= product.meta['image_count']; i++) {
        let version: any = i;
        if (i < 10) {
          version = `0${i}`;
        }
        if (product.slug.includes('glasses-straps')) {
          images.push(generateAccessoriesImageMultiple(product.slug, BackgroundColor.Grey, version));
        } else {
          images.push(generateAccessoriesImageMultiple(product.slug, BackgroundColor.White, version));
        }
      }
    }

    return images;
  }, [product]);

  const isFlexAccessoryClip = useMemo(() => {
    return product && product?.slug ? (
      product.slug.includes('flex-accessory-clip') || product.slug.includes('spex-glasses')
    ) : false;
  }, [product]);

  const colorMap: Record<string, string> = {
    'black': '#000000',
    'blue': '#0000FF',
    'brown': '#A52A2A',
    'burgundy': '#800020',
    'dark-green': '#006400',
    'fluorescent-yellow': '#CCFF00',
    'green': '#008000',
    'grey': '#808080',
    'navy': '#000080',
    'orange': '#FFA500',
    'pink': '#FFC0CB',
    'red': '#FF0000',
    'white': '#FFFFFF',
    'yellow': '#FFFF00'
  };

  const glassesStrapsColors = useMemo(() => {
    const products = accessories.products;
    const straps = products.filter((product: any) => product.slug.includes('glasses-straps'));
    return straps.map((product: any) => getStrapColorName(product.slug));
  }, [accessories]);

  const currentStrapColor = useMemo(() => {
    if (isGlassesStraps) {
      return getStrapColorName(product.slug);
    }
  }, [isGlassesStraps, product]);

  return product ? (
    <div className="w-full relative">
      <Head>
        <meta property="og:type" content="product" />
        <meta property="og:title" content={`${getProductName(product.name)}`} />
        <meta property="og:site_name" content="Dresden" />
        <meta property="og:image:width" content="400" />
        <meta property="og:image:height" content="400" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:label2" content="Availability" />
        <meta name="twitter:data2" content="In stock" />
        <meta property="og:availability" content="instock" />
        <meta property="product:availability" content="instock" />
        <meta property="product:condition" content="new" />
        <meta
          name="twitter:title"
          content={getProductName(product.name)}
        />
        <meta
          name="og:title"
          content={getProductName(product.name)}
        />
        <meta
          name="description"
          content={`${getProductName(product.name)}  are on this page with advantageous price and Dresden Vision assurance. Shop ${getProductName(product.name)} online.`}
        />
        <meta
          property="og:description"
          content={`${getProductName(product.name)} are on this page with advantageous price and Dresden Vision assurance. Shop ${getProductName(product.name)} online.`}
        />
        <meta
          name="twitter:description"
          content={`${getProductName(product.name)} are on this page with advantageous price and Dresden Vision assurance. Shop ${getProductName(product.name)} online.`}
        />
        <meta property="og:image" content={`${getSeoImage()}`} />
        <meta property="twitter:image" content={`${getSeoImage()}`} />
        <title>{`${getProductName(product.name)} | Dresden Vision`}</title>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org/',
              '@type': 'Product',
              name: getProductName(product.name),
              image: [cdnImage],
              description: `${getProductName(product.name)} are on this page with advantageous price and Dresden Vision assurance. Shop ${getProductName(product.name)} online.`,
              brand: {
                '@type': 'Brand',
                name: 'DRESDEN VISION'
              },

              offers: {
                '@type': 'Offer',
                url:
                  `${process.env.NEXT_PUBLIC_SITE_URL}` +
                  `${router.locale}` +
                  `${router.asPath}`,
                priceCurrency: getLocalCurrency(router.locale),
                price: priceToNumber(product.price),
                itemCondition: 'https://schema.org/UsedCondition',
                availability: 'https://schema.org/InStock'
              }
            })
          }}
        />
      </Head>
      <div className="max-w-[1224px] mx-auto px-4 pt-6">
        <Breadcrumb data={data} />
        <div className="grid grid-cols-2 gap-x-10 xlx:grid-cols-1 mt-4 pb-12">
          <div className="w-full sticky top-[20px] z-[22] xlx:static">
            <div className={cx("relative w-full flex items-center justify-center bg-[#f6f8fa] border-2 border-brand-grey125 rounded xsmx:w-full xlx:mx-auto", {
              "h-[560px] xsmx:h-[480px]": isFlexAccessoryClip,
              "h-[420px] lg:!h-[520px]": !isFlexAccessoryClip
            })}>
              <div className='w-full h-full'>
                {images?.length == 1 && (
                  <Image
                    quality={75}
                    objectFit="contain"
                    priority
                    layout="fill"
                    src={images?.[0]}
                    alt={getAccessorieName(product.name)}
                  />
                )}
                <div className={cx("group relative mx-auto h-full", {
                  "product-acc-height": isFlexAccessoryClip,
                  "max-w-[650px]": !isFlexAccessoryClip
                })}>
                  {images?.length > 1 && (
                    <>
                      <button className="prevel absolute top-2/4 left-5 z-50 hidden h-8 w-8 -translate-y-2/4 rotate-90 transform items-center justify-center rounded-full bg-brand-grey125 opacity-50 transition-all hover:opacity-100 group-hover:flex mdx:hidden xsmx:left-3">
                        <Arrow fill="#5c615cd3" width={14} height={8} />
                      </button>
                      <button className="nextel absolute top-2/4 right-5 z-50 hidden h-8 w-8 -translate-y-2/4 -rotate-90 transform items-center justify-center rounded-full bg-brand-grey125 opacity-50 transition-all hover:opacity-100 group-hover:flex mdx:hidden xsmx:right-3">
                        <Arrow fill="#5c615cd3" width={14} height={8} />
                      </button>
                      <Swiper
                        modules={[Navigation, Thumbs]}
                        loop
                        className="w-full h-full"
                        onSwiper={setSwiperInstance}
                        navigation={{
                          nextEl: '.nextel',
                          prevEl: '.prevel'
                        }}
                        slidesPerView={1}
                      >
                        {images.map((item, index) => (
                          <SwiperSlide
                            className={cx("relative flex ", {
                              "h-[280px] w-full lg:h-[420px]": !isFlexAccessoryClip,
                            })}
                            key={index}
                          >
                            <Image
                              quality={89}
                              objectFit={isFlexAccessoryClip ? "cover" : "contain"}
                              priority
                              layout="fill"
                              loader={imageLoader}
                              blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mO8Uw8AAj0BXe1cD0IAAAAASUVORK5CYII="
                              src={item}
                              alt={product.name}
                            />
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    </>
                  )}
                </div>
              </div>
            </div>

            {images.length > 1 && (
              <div className="mt-2 lgx:hidden">
                {images.map((item, index) => (
                  <button
                    onClick={() => navigateToCarouselSlide(index)}
                    key={index}
                    className={`relative mr-[10px] bg-[#f7fafb] h-14 w-20 rounded-md inline-block border ${swiperActiveIndex == index ? 'border-brand-black75' : 'border-brand-grey125'
                      }`}
                  >
                    <Image
                      quality={89}
                      placeholder="blur"
                      blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mO8Uw8AAj0BXe1cD0IAAAAASUVORK5CYII="
                      layout="fill"
                      objectFit={isFlexAccessoryClip && index == 0 ? 'contain' : 'cover'}
                      src={item}
                      loader={imageLoader}
                      className="rounded-md"
                      alt={`${product.name} Contact Lens`}
                      priority
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className={isFeltCase ? '' : "border-b border-brand-grey125"}>
              <h1 className="font-bold text-black text-3xl smx:text-2xl font-sans mb-4 xlx:mt-4">
                {getAccessorieName(product.name)}
              </h1>
              <h5 className="font-normal text-black text-md font-sans mb-8">
                <PriceBadge
                  key={router.locale}
                  price={visiblePrice.salePrice}
                  regularPrice={visiblePrice.regularPrice}
                />
              </h5>
            </div>
            <div>
              {isFeltCase && (
                <div className="flex flex-col gap-4 mb-2">
                  <div className="font-sans text-xs font-medium tracking-wider text-black">
                    COLOUR
                  </div>
                  <div className="hide-scroll flex tsm:flex-wrap overflow-auto w-full gap-x-1.5 gap-y-2">
                    {feltCasesColors.map((e: any, i: number) => {
                      if (!e) {
                        return null;
                      }

                      return (
                        <UITooltip key={i} id={`color-tooltip-${i}`} delay="20" place="top" message={slugToString(e)} messageWrapperClassName={'text-black'} tooltipWrapperClassName={'!rounded-[4px] border !border-[#E2E2E2] border-solid !bg-white shadow-tooltipSecondary white-tip'}>
                          <div
                            key={i}
                            onClick={() => {
                              router.push(`/shop/accessories/felt-case-${e}`);
                            }}
                            className={`flex items-center justify-center min-w-[30px] h-[30px] cursor-pointer rounded-full ${feltCaseColor == e
                              ? ''
                              : 'border border-brand-grey200 p-[3px]'
                              }`}>
                            {feltCaseColor == e ?
                              <div
                                className={`w-full h-full rounded-full flex justify-center items-center`}
                                style={{ background: `${getColorHex(e)}` }}>
                                <Checkmark height={27} width={15} fill={e === "white" ? '#000' : '#FFF'} />
                              </div>
                              :
                              <div
                                className={`w-[16px] h-[16px] rounded-full`}
                                style={{ background: `${getColorHex(e)}` }}>
                              </div>
                            }
                          </div>
                        </UITooltip>
                      )
                    }
                    )}
                  </div>
                </div>
              )}
              {isGlassesStraps && (
                <div className="flex flex-col gap-4 mb-2">
                  <div className="font-sans text-xs font-medium tracking-wider text-black">
                    COLOUR
                  </div>
                  <div className="hide-scroll flex tsm:flex-wrap overflow-auto w-full gap-x-1.5 gap-y-2">
                    {glassesStrapsColors.map((color: string, i: number) => (
                      <UITooltip
                        key={i}
                        id={`strap-color-tooltip-${i}`}
                        delay="20"
                        place="top"
                        message={slugToString(color)}
                        messageWrapperClassName={'text-black'}
                        tooltipWrapperClassName={'!rounded-[4px] border !border-[#E2E2E2] border-solid !bg-white shadow-tooltipSecondary white-tip'}
                      >
                        <div
                          onClick={() => {
                            router.push(`/shop/accessories/glasses-straps-${color}`);
                          }}
                          className={`flex items-center justify-center min-w-[30px] h-[30px] cursor-pointer rounded-full ${currentStrapColor === color
                            ? ''
                            : 'border border-brand-grey200 p-[3px]'
                            }`}
                        >
                          {currentStrapColor === color ? (
                            <div
                              className="w-full h-full rounded-full flex justify-center items-center"
                              style={{ background: colorMap[color] }}
                            >
                              <Checkmark height={27} width={15} fill={color === "white" ? '#000' : '#FFF'} />
                            </div>
                          ) : (
                            <div
                              className="w-[16px] h-[16px] rounded-full"
                              style={{ background: colorMap[color] }}
                            />
                          )}
                        </div>
                      </UITooltip>
                    ))}
                  </div>
                </div>
              )}
              <Button
                disabled={
                  process.browser && !getSession()
                    ? true
                    : isLoad
                      ? true
                      : false
                }
                onClick={() => {
                  const session = getSession();
                  const extraData = {
                    productName: product.name,
                    productLink: router.asPath,
                    productType: 'accessories',
                    imagePath: cdnImage
                  };

                  if (session) {
                    dispatch(setLoading(true));
                    dispatch(addToCart({
                      type: 'simple',
                      variables: {
                        queueId: uuidV4,
                        input: {
                          productId: product.post_id,
                          clientMutationId:
                            getSession(),
                          quantity: 1,
                          extraData: JSON.stringify(extraData)
                        }
                      }
                    }));

                    // Google Analytics dataLayer
                    addDataLayer({
                      event: 'addToCart',
                      ecommerce: {
                        currencyCode: getLocalCurrency(router.locale),
                        add: {
                          products: [
                            {
                              name: getProductName(product.name),
                              price: priceToNumber(product.price),
                              category: 'Accessories',
                              quantity: 1
                            }
                          ]
                        }
                      }
                    });

                    // Meta Conversion API tracking
                    sendAddToCartEvent({
                      product_id: `accessories-${product.slug}`,
                      product_name: getProductName(product.name),
                      value: Number(priceToNumber(product.price)),
                      currency: getLocalCurrency(router.locale),
                      content_category: 'Accessories',
                      contents: [
                        {
                          id: `accessories-${product.slug}`,
                          quantity: 1,
                          item_price: Number(priceToNumber(product.price))
                        }
                      ]
                    });
                  }
                }}
                className={cx('mdx:sticky mdx:bottom-3 text-center bg-brand-black50 justify-center my-auto !px-6  mt-8',
                  { 'mdx:w-full w-[355px]': isFeltCase, 'w-full': !isFeltCase }
                )}
                textClass="font-medium text-white text-sm">
                <span className="flex items-center">
                  {isLoad && (
                    <span className="mr-2 mt-[1px]">
                      <Spinner fill="white" />
                    </span>
                  )}
                  <span>
                    Add to cart {formatVisiblePrice(
                      priceToNumber(product.price) as number,
                      bagItems.currency
                    )}
                  </span>
                </span>
              </Button>
            </div>

            <AfterPayInfo total={visiblePrice.salePrice} />
            <ZipPayInfo total={visiblePrice.salePrice} />
          </div>
        </div>

        {productDetail &&
          (
            <UITabs wrapperClassName='mt-[50px]'>
              <UITab wrapperClassName="pt-6" title="Details">
                <div className="flex justify-between w-full gap-8">
                  <div className="font-normal text-[16px] leading-[18px] text-[#2A2A2A] tracking-[0.02em]"
                    dangerouslySetInnerHTML={{
                      __html: productDetail.description.replace(/\n/g, '<br/>')
                    }}
                  />
                </div>
              </UITab>
              <UITab wrapperClassName={''} title={''}></UITab>
            </UITabs>
          )
        }

        {productDetail &&
          (
            <div className="lg:hidden block">
              <UIAccordion title="Details" defaultOpen={true}>
                <div className="flex flex-col">
                  <div className="font-normal text-[16px] leading-[18px] text-[#2A2A2A] tracking-[0.02em]"
                    dangerouslySetInnerHTML={{
                      __html: productDetail.description.replace(/\n/g, '<br/>')
                    }}
                  />
                </div>
              </UIAccordion>
            </div>
          )
        }
      </div>
      <LazyHydrate whenVisible>
        <CategorySeoText seoBlock={seoBlock} />
      </LazyHydrate>
      <FAQJson faq={seoBlock.faq} />
      {isImported && cdnImage && (
        <ProdSliderModal
          images={[cdnImage]}
          alt={getProductName(product.name)}
          setModalOpen={setModalOpen}
          setIsImported={setIsImported}
          modalOpen={modalOpen}
        />
      )}
    </div>
  ) : null;
}